const WirelessMonitoringSystem = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/wireless-system.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            Wireless Tire Pressure Monitoring Systems (TPMS): Why Modern Drivers Need Them
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Stay safer, save fuel, and extend your tire lifespan with modern wireless TPMS technology — a smart upgrade every vehicle can benefit from.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            Tires are the foundation of safe driving — and proper tire pressure plays the biggest role in maintaining stability, performance, and efficiency. 
            That’s where a <strong>Wireless Tire Pressure Monitoring System (TPMS)</strong> comes in. Unlike traditional manual pressure checks, wireless TPMS continuously monitors each tire and alerts you instantly if pressure drops or rises beyond safe limits.
          </p>

          <p className="mb-6">
            Whether you drive a sedan, SUV, truck, or EV, a wireless TPMS helps prevent blowouts, improves fuel economy, and ensures a smoother driving experience. Here’s everything you need to know.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. What Is a Wireless TPMS?
            </h2>
            <p>
              A Wireless TPMS is a modern digital device that monitors tire air pressure using external or internal sensors. These sensors wirelessly send real-time data to a display unit or smartphone app, warning you of under-inflation, over-inflation, or temperature spikes.
            </p>
            <p className="mt-2">
              Most modern systems use Bluetooth or RF signals and can be installed without professional tools, making them popular among daily drivers, commercial fleets, and off-road enthusiasts.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. Why Proper Tire Pressure Matters
            </h2>
            <p>
              Driving with incorrect tire pressure can lead to:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Reduced fuel efficiency</strong> — Underinflated tires increase rolling resistance.</li>
              <li><strong>Uneven tread wear</strong> — Shortens tire lifespan and causes premature replacement.</li>
              <li><strong>Poor handling</strong> — Reduced stability during braking and turning.</li>
              <li><strong>Greater blowout risk</strong> — Especially at high speeds or in hot climates.</li>
            </ul>
            <p>
              A wireless TPMS solves these issues by giving constant, accurate pressure readings without needing manual checks.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. Types of Wireless TPMS Sensors
            </h2>
            <p>There are two primary types of wireless TPMS sensors:</p>

            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>
                <strong>External Sensors:</strong>  
                Easy to install on valve stems, affordable, and battery-powered. Ideal for everyday vehicles.
              </li>
              <li>
                <strong>Internal Sensors:</strong>  
                Installed inside the tire for higher accuracy, durability, and theft protection. Best for long-term use.
              </li>
            </ul>

            <p>Both options provide real-time pressure data, temperature tracking, and instant alerts.</p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. Key Benefits of Wireless TPMS
            </h2>
            <p>Modern wireless systems offer several major advantages:</p>

            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Instant alerts</strong> for low or high tire pressure.</li>
              <li><strong>Improved fuel economy</strong> by maintaining optimal pressure.</li>
              <li><strong>Enhanced driving safety</strong> with better control and traction.</li>
              <li><strong>Extended tire life</strong> by preventing uneven wear.</li>
              <li><strong>No complicated wiring</strong> — simple plug-and-play installation.</li>
              <li><strong>Smartphone connectivity</strong> for real-time monitoring.</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. Are Wireless TPMS Systems Accurate?
            </h2>
            <p>
              Yes — modern wireless TPMS systems are extremely accurate, with most offering a reading tolerance of ±1 PSI. Internal sensors generally provide the highest accuracy since they are protected from external factors like dust or weather.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. Ideal for EVs, SUVs, and Long-Distance Drivers
            </h2>
            <p>
              Because EVs and SUVs are heavier, they put more stress on tires. Wireless TPMS helps:
            </p>

            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Improve EV range</li>
              <li>Balance load distribution</li>
              <li>Reduce heat buildup during long drives</li>
              <li>Prevent high-speed blowouts</li>
            </ul>

            <p>It's also a must-have for road trips and commercial vehicles.</p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              7. When Should You Replace TPMS Sensors?
            </h2>
            <p>
              Most wireless TPMS sensors last between <strong>3–7 years</strong>, depending on battery quality and driving conditions. Replace sensors when:
            </p>

            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Battery warnings appear</li>
              <li>You receive inaccurate pressure readings</li>
              <li>The sensor stops transmitting data</li>
              <li>A sensor becomes damaged or corroded</li>
            </ul>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              A Wireless Tire Pressure Monitoring System is one of the smartest investments you can make in your vehicle’s safety and performance. 
              With real-time monitoring, instant alerts, and extended tire life, wireless TPMS technology offers convenience, protection, and peace of mind on every journey. 
              Whether you drive an EV, SUV, or daily commuter car, upgrading to a wireless TPMS ensures you stay in control — mile after mile.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">TPMS</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Safety</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Wireless Sensors</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Maintenance</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Smart Car Accessories</p>
        </div>
      </section>
    </div>
  );
};

export default WirelessMonitoringSystem;